import useIntersectionObserver from "@react-hook/intersection-observer"
import React, { FC, useEffect, useRef } from "react"
import { useResizeDetector } from "react-resize-detector"
import { up } from "styled-breakpoints"
import styled from "styled-components"

import { getElementDimensions } from "../../utilities/helpers"
import Slide from "./slide"

export type SliderProps = {
  autoplay?: boolean
  autoplayDirection?: "ltr" | "rtl"
  autoplaySpeed?: number
  children?: any
  className?: string
  gap?: {
    default?: number
    lg?: number
    md?: number
  }
  hasDivider?: boolean
  noDrag?: boolean
  ref?: any
  width?: {
    default?: number
    lg?: number
    md?: number
  }
}

const SliderStage = styled.div<SliderProps>`
  all: initial;
  position: relative;
  display: inline-flex;
  width: auto;
  height: 100%;
  max-height: 100vh;
  cursor: ${(props) => (props.noDrag ? "default" : "grab")};
  transform: translate3d(0, 0, 0);
  will-change: transform, scale;

  .slide-outer {
    display: flex;
    align-items: center;
    min-width: ${(props) =>
      props?.width?.default && props?.gap?.default
        ? props.width.default + props.gap.default + "px"
        : "125px"};
    ${up("md")} {
      min-width: ${(props) =>
        props?.width?.md && props?.gap?.md
          ? props.width.md + props.gap.md + "px"
          : "150px"};
    }
    ${up("lg")} {
      min-width: ${(props) =>
        props?.width?.lg && props?.gap?.lg
          ? props.width.lg + props.gap.lg + "px"
          : "190px"};
    }

    opacity: 1;
    transition: opacity 0.36s ease-in-out;
  }

  .new {
    opacity: 0;
  }
`

const SliderWrapper = styled.div<SliderProps>`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  overflow: hidden;
  text-align: center;
`
export const Slider: FC<SliderProps> = ({
  autoplay,
  autoplayDirection,
  autoplaySpeed,
  children,
  className,
  gap,
  noDrag,
  width,
}) => {
  const isAddingSlide = useRef(false)
  const sliderRef = useRef<HTMLElement | null>(null)
  const sliderWrapperRef = useRef<HTMLElement | null>(null)

  const animationRef = useRef(0)
  const hasAutoplay = useRef(false)
  const hasOverflow = useRef(false)

  const slideRef: Array<any> = []
  const transitionSpeed = autoplaySpeed || 4000
  const translateTarget = useRef(0)

  const { isIntersecting } = useIntersectionObserver(sliderWrapperRef)

  const isVisibleNow = isIntersecting

  const isVisible = useRef(isVisibleNow)

  const isInitialized = useRef(false)

  const wrapperWidth = useRef(0)

  autoplay = autoplay ?? false

  React.Children.map(children, () => {
    slideRef.push(useRef<HTMLElement | null>(null))
  })

  // Clean-up old animationFrame and reset transforms
  // useful if restarting slider on resize for e.g
  const resetStopSlider = () => {
    if (sliderRef?.current) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      translateTarget.current = 0
      sliderRef.current.style.transform = "translate3d(0px, 0px, 0px)"
    }
  }

  const initSlider = () => {
    // SAVE THE WRAPPER WIDTH TO CHECK FOR RESIZE CHANGE
    const wrapperDimensions = getElementDimensions(sliderWrapperRef.current)
    wrapperWidth.current = wrapperDimensions.width

    // GET THE WIDTH AND CHECK IF IT IS WIDER THAN THE VIEWPORT
    const { width } = getElementDimensions(sliderRef.current)
    hasOverflow.current = width >= window.innerWidth

    if (sliderRef?.current && !hasOverflow.current) {
      sliderRef.current.style.cursor = "default"

      resetStopSlider()

      return false
    } else if (sliderRef?.current && !noDrag) {
      sliderRef.current.style.cursor = "grab"
    }

    if (autoplay) {
      hasAutoplay.current =
        (width >= window.innerWidth && autoplay && isVisible.current) || false
    }

    // START THE RUNTIME LOOP IF SLIDER WIDTH IS OVER WINDOW INNER WIDTH
    if (hasOverflow.current && isVisible.current) {
      animationRef.current = requestAnimationFrame(sliderRuntime)
    }
  }

  const sliderRuntime = () => {
    if (sliderRef.current && isVisible.current) {
      // TO IMPROVE - We should rethink fetching the slides directly from the DOM
      const allSlides = sliderRef.current.getElementsByClassName("slide-outer")
      const origSlides = sliderRef.current.getElementsByClassName(
        "slide-outer original",
      )
      const lastSlide = allSlides[allSlides.length - 1] as HTMLElement
      const slideWidth = getElementDimensions(lastSlide).width

      const shouldAddAfter = translateTarget.current <= -slideWidth
      const shouldAddBefore = translateTarget.current >= slideWidth / 2

      const shouldAddDuplicate =
        (shouldAddAfter || shouldAddBefore) &&
        lastSlide &&
        sliderRef.current.getElementsByClassName("slide-outer new").length === 0

      const slideToDup = shouldAddBefore
        ? origSlides[origSlides.length - 1]
        : origSlides[0]

      if (shouldAddDuplicate && slideToDup && !isAddingSlide.current) {
        isAddingSlide.current = true

        // DOM CHANGES
        const dupSlide = slideToDup.cloneNode(true) as HTMLElement
        dupSlide.classList.add("new")
        slideToDup.remove()

        if (shouldAddBefore) {
          sliderRef?.current?.prepend(dupSlide)
        } else {
          sliderRef?.current?.append(dupSlide)
        }

        // UPDATE SLIDER TRANSLATE TO MIRROR DOM CHANGES
        if (shouldAddBefore) {
          translateTarget.current = -translateTarget.current
        } else {
          translateTarget.current = 0
        }

        isAddingSlide.current = false

        // FADE-IN SLIDE ANIM
        setTimeout(() => {
          dupSlide.classList.remove("new")
        }, 60)
      }

      // TRANSFORMS
      if (hasAutoplay.current) {
        // AUTOPLAY
        if (Math.abs(translateTarget.current) < slideWidth) {
          /**
           * Gets the available frames (requestAnimationFrame is 60 frames by second)
           * from the set transitionSpeed and calculates the distance one frame should translate by
           *
           **/
          const frameTranslate = Math.abs(
            slideWidth / ((transitionSpeed * 60) / 1000),
          )

          translateTarget.current =
            autoplayDirection === "rtl"
              ? translateTarget.current + frameTranslate
              : translateTarget.current - frameTranslate
        }
      }

      sliderRef.current.style.transform = `translate3d(${translateTarget.current}px, 0px, 0px)`

      // LOOP IT
      animationRef.current = requestAnimationFrame(sliderRuntime)
    } else {
      resetStopSlider()
    }
  }

  const startSlider = () => {
    if (isVisible.current) {
      resetStopSlider()
      initSlider()
    } else {
      resetStopSlider()
    }
  }

  useResizeDetector({
    handleHeight: false,
    onResize: () => {
      if (!isInitialized.current) {
        return
      }

      const { width } = getElementDimensions(sliderWrapperRef.current)

      if (width !== wrapperWidth.current) {
        startSlider()
        wrapperWidth.current = width
      }
    },
    refreshMode: "debounce",
    refreshRate: 440,
    skipOnMount: true,
    targetRef: sliderWrapperRef,
  })

  useEffect(() => {
    if (!isInitialized.current) {
      return
    }

    if (isVisibleNow !== isVisible.current) {
      isVisible.current = isVisibleNow

      if (isVisibleNow) {
        if (sliderWrapperRef.current) {
          sliderWrapperRef.current.onselectstart = () => false
        }

        startSlider()
      } else {
        resetStopSlider()
      }
    }
  }, [isVisibleNow])

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true

      startSlider()
    }
  }, [])

  const classes = className || ""

  return (
    <SliderWrapper
      className={classes + " SliderWrapper"}
      ref={sliderWrapperRef}>
      <SliderStage
        autoplay={autoplay}
        className="SliderStage"
        gap={gap}
        noDrag={noDrag}
        ref={sliderRef}
        width={width}>
        {children && React.Children.count(children) >= 1 ? (
          <>
            {React.Children.map(children, (child, index) => {
              return (
                <div className="slide-outer original" ref={slideRef[index]}>
                  <Slide child={child} gap={gap} width={width} />
                </div>
              )
            })}
          </>
        ) : (
          <>
            {
              <div className="slide-outer single">
                <Slide
                  child={children}
                  gap={gap}
                  ref={slideRef[0]}
                  width={width}
                />
              </div>
            }
          </>
        )}
      </SliderStage>
    </SliderWrapper>
  )
}

export default Slider
